import { inject, Injectable } from "@angular/core";

import { WidgetOutputMode } from "@main/reporting.v3/core/reporting-models";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { IStaticVariable } from "@main/reporting.v3/core/variables/variable-models";
import { WidgetHub, WidgetReference } from "@main/reporting.v3/core/widget-hub/models";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { WidgetInputAlias } from "@main/reporting.v3/widget/models/widget-input-alias.enum";
import { WIDGET_HUB } from "@main/reporting.v3/core/widget-hub/constants/widget-hub.constants";


@Injectable()
export class WidgetHtmlEmbeddedWidgetsService {

    private widgetHub: WidgetHub = inject(WIDGET_HUB);

    public refreshWidgetOutputs(output: WidgetOutputMode): void {
        const widgets: WidgetReference[] = this.widgetHub.getWidgets();

        widgets.forEach(component => component.setInput(WidgetInputAlias.Output, output));
    }

    public refreshWidgetStaticVariables(variables: IStaticVariable[]): void {
        const widgets: WidgetReference[] = this.widgetHub.getWidgets();

        widgets.forEach(component => component.setInput(WidgetInputAlias.StaticVariables, variables));
    }

    public async replaceWidgetsByPdfTemplates(root: HTMLElement): Promise<void> {
        // Get all PDF configurations, in a record configurations by widget source id.
        const record: Record<string, JsReportConfiguration[]> = await this.collectPdfConfigurations();

        // Foreach widget source id, replace all corresponding widgets by its PDF template.
        Object.keys(record).forEach(widgetSourceId => {

            // Get all PDF configurations corresponding to this widget source id.
            const configurations: JsReportConfiguration[] = record[widgetSourceId];

            // Get all widgets with this widget source id as container id.
            const selector: string = `[id="${widgetSourceId}"]`;
            const elements: Element[] = Array.from(root.querySelectorAll(selector));

            const length: number = Math.min(configurations.length, elements.length);

            // Replace each widget by the corresponding PDF template.
            for (let i: number = 0; i < length; i++) {
                const configuration: JsReportConfiguration = configurations[i];

                if (!!configuration) {
                    // If widget is visible in PDF mode, change its outer HTML by the PDF template.
                    const html: string = configuration.template;
                    const css: string = configuration.cssRules;

                    elements[i].outerHTML = `${html}<style>${css}</style>`;

                    // Otherwise, remove the widget.
                } else elements[i].remove();
            }
        })
    }

    private async collectPdfConfigurations(): Promise<Record<string, JsReportConfiguration[]>> {
        const components: WidgetReference[] = this.widgetHub.getWidgets();

        // Get the promises corresponding to widget PDF configuration resolution.
        const promises: Promise<[string, JsReportConfiguration]>[] = components
            .map(component => this.getPdfConfiguration(component));

        // Resolve all widget PDF configurations in parallel. Omit error null results.
        const results: [string, JsReportConfiguration][] = await Promise.all(promises);

        const filtered: [string, JsReportConfiguration][] = results.filter(result => !!result);

        // Create a record of widget PDF configurations by widget source id.
        const record: Record<string, JsReportConfiguration[]> = {};

        filtered.forEach(result => {
            const [widgetSourceId, configuration] = result;

            record[widgetSourceId] ??= [];
            record[widgetSourceId].push(configuration);
        });

        return record;
    }

    private async getPdfConfiguration(component: WidgetReference): Promise<[string, JsReportConfiguration]> {
        const widget: IWidgetComponent & Partial<IPdfExportable> = component.instance;

        if (!widget?.widgetSourceId) return null;

        const widgetSourceId: string = widget.widgetSourceId;

        if (!widget?.pdfViewVisible) return [widgetSourceId, null];

        const configuration: JsReportConfiguration = await widget.getPdfConfiguration();

        return [widgetSourceId, configuration];
    }
}